import { CaretSortIcon, CheckIcon } from '@radix-ui/react-icons'
import { Link } from '@tanstack/react-router'
import { BadgeCheckIcon, Bell, ChevronsUpDown, Globe, LogOut, Settings } from 'lucide-react'
import { useState } from 'react'
import { UserAvatarInfo } from '@/components/_shared/user-avatar-info'
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from '@/components/ui/command'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	useSidebar,
} from '@/components/ui/sidebar'
import { useLogout } from '@/hooks/use-logout'
import { cn } from '@/lib/utils'
import type { AuthUser } from '@/stores/types'

const languages = [
	{
		label: 'English',
		value: 'en',
	},
	{
		label: 'French',
		value: 'fr',
	},
	{
		label: 'German',
		value: 'de',
	},
	{
		label: 'Spanish',
		value: 'es',
	},
	{
		label: 'Portuguese',
		value: 'pt',
	},
	{
		label: 'Russian',
		value: 'ru',
	},
	{
		label: 'Japanese',
		value: 'ja',
	},
	{
		label: 'Korean',
		value: 'ko',
	},
	{
		label: 'Chinese',
		value: 'zh',
	},
] as const

type LanguageCode = (typeof languages)[number]['value']

interface Nav_User_props {
	user: Partial<AuthUser>
}

export function NavUser(props: Nav_User_props) {
	const { user } = props
	const { isMobile } = useSidebar()
	const { handleLogout } = useLogout()
	const [language, setLanguage] = useState<LanguageCode>('en')
	const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false)

	return (
		<>
			<SidebarMenu>
				<SidebarMenuItem>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<SidebarMenuButton
								size="lg"
								className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
							>
								<UserAvatarInfo user={user} allowCopy={false} />
								<ChevronsUpDown className="size-4 ml-auto" />
							</SidebarMenuButton>
						</DropdownMenuTrigger>
						<DropdownMenuContent
							className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
							side={isMobile ? 'bottom' : 'right'}
							align="end"
							sideOffset={4}
						>
							<DropdownMenuLabel className="p-0 font-normal">
								<div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
									<UserAvatarInfo
										user={user}
										textClassName="grid flex-1 text-left text-sm leading-tight"
									/>
								</div>
							</DropdownMenuLabel>

							<DropdownMenuSeparator />
							<DropdownMenuGroup>
								<DropdownMenuItem asChild>
									<Link to="/settings">
										<BadgeCheckIcon className="bg-blue-500 text-white rounded-xl" />
										Profile
									</Link>
								</DropdownMenuItem>
								<DropdownMenuItem onClick={() => setIsLanguageModalOpen(true)}>
									<Globe className=" h-4 w-4" />
									Language
									<CaretSortIcon className="ml-auto h-4 w-4 opacity-50" />
								</DropdownMenuItem>
								<DropdownMenuItem asChild>
									<Link to="/settings/notifications">
										<Bell />
										Notifications
									</Link>
								</DropdownMenuItem>
								<DropdownMenuItem asChild>
									<Link to="/settings">
										<Settings />
										Settings
									</Link>
								</DropdownMenuItem>
							</DropdownMenuGroup>
							<DropdownMenuSeparator />
							<DropdownMenuItem
								className="text-destructive focus:text-destructive gap-3 px-4 py-3 cursor-pointer"
								onClick={() => {
									handleLogout()
								}}
							>
								<LogOut className="w-4 h-4" />
								<span>Logout</span>
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</SidebarMenuItem>
			</SidebarMenu>

			{/* Language Selection Modal */}
			<Dialog open={isLanguageModalOpen} onOpenChange={setIsLanguageModalOpen}>
				<DialogContent className="sm:max-w-[425px]">
					<DialogHeader>
						<DialogTitle>Select Language</DialogTitle>
					</DialogHeader>
					<div className="py-4">
						<Command>
							<CommandInput placeholder="Search language..." />
							<CommandEmpty>No language found.</CommandEmpty>
							<CommandGroup>
								<CommandList>
									{languages.map((lang) => (
										<CommandItem
											value={lang.label}
											key={lang.value}
											onSelect={() => {
												setLanguage(lang.value)
												setIsLanguageModalOpen(false)
											}}
										>
											<CheckIcon
												className={cn(
													'mr-2 h-4 w-4',
													lang.value === language ? 'opacity-100' : 'opacity-0',
												)}
											/>
											{lang.label}
										</CommandItem>
									))}
								</CommandList>
							</CommandGroup>
						</Command>
					</div>
				</DialogContent>
			</Dialog>
		</>
	)
}
